import InvitationService from "@services/invitation.service";
import Config from "@config";
import StudyService from "@services/study.service.js";
import { getSession } from "@hooks/useSession.js";
import NavigationService from "@services/navigation.service.js";
import EmailController from "./email.controller.js";
import AuthService from "@services/auth.service.js";
import EntityService from "@services/entity.service.js";
import State, { STATE, CurrentStudyState, CurrentEntityState } from "@state";
import { sha256 } from "@/utils/crypto.js";




/**
 * Fetches invitation details from the backend using the frontend service.
 * This is a FRONTEND controller function.
 * @param {string} invitationId - The ID of the invitation.
 * @param {string} authId - The authentication ID associated with the invitation.
 * @returns {Promise<object>} An object containing the invitation details or an error.
 */
const getInvitationDetails = async (
  invitationId,
  authId,
  status = ["pending", "accepted", "revoked", "read", "joined", "declined"]
) => {
  try {
    if (!invitationId || !authId) {
      return {
        success: false,
        message: "Missing invitationId or authId in parameters.",
        errorCode: "invitation-details-missing-params",
        errorKey: 100009,
      };
    }

    console.log('=== status === invitation.controller.js === key: 081227 ===');
    console.dir(status, { depth: null, colors: true })
    console.log('=================================');

    const result = await InvitationService.getInvitationDetails(
      invitationId,
      authId,
      status
    );
    console.log("=== result === invitation.controller.js === key: 390596 ===");
    console.dir(result, { depth: null, colors: true });
    console.log("=================================");
    return result;
  } catch (error) {
    console.error("Frontend Controller error in getInvitationDetails:", error);
    return {
      success: false,
      message: "Unexpected error while fetching invitation details.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "invitation-details-controller-error",
      errorKey: 100010,
    };
  }
};

/**
 * Sends an invitation for a new user (public/unauthenticated version).
 * Performs basic validation and forwards the data to the frontend service.
 * @param {object} invitationData - Raw form data (email, firstname, lastname, invitation_type).
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const sendPublicInvitation = async (invitationData) => {
  try {
    if (!invitationData) {
      return {
        success: false,
        message: "No invitation data provided for public send.",
        errorCode: "public-invitation-no-data",
        errorKey: 100018,
      };
    }

    let { email, firstname, lastname, invitation_type } = invitationData;

    if (typeof email === "string") {
      email = email.toLowerCase();
    }

    if (!email || !invitation_type) {
      return {
        success: false,
        message: "Missing email or invitation type for public send.",
        errorCode: "public-invitation-invalid-data",
        errorKey: 100019,
      };
    }

    const payload = { email, firstname, lastname, invitation_type };
    const result = await InvitationService.sendPublicInvitation(payload);

    if (!Config.IN_PROD) {
      console.log("=== Public Invitation Result (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("=================================");
    }

    return result;
  } catch (error) {
    console.error("Frontend Controller error in sendPublicInvitation:", error);
    return {
      success: false,
      message: "Unexpected error while sending public invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "public-invitation-controller-error",
      errorKey: 100020,
    };
  }
};

// A supprimer une fois que sendPublicInvitation sera protegé
const sendProtectedInvitation = async (invitationData) => {
  try {
    if (!invitationData) {
      return {
        success: false,
        message: "No invitation data provided for protected send.",
        errorCode: "protected-invitation-no-data",
        errorKey: 100018,
      };
    }

    let {
      email,
      firstname,
      lastname,
      contact_name, 
      invitation_type,
      send_by_user_id,
      entity_id,
      investigator_id,
      study_id,
      site_id,
      phone_number,
    } = invitationData;

    if (typeof email === "string") {
      email = email.toLowerCase();
    }

    if (!email || !invitation_type) {
      return {
        success: false,
        message: "Missing email or invitation type for public send.",
        errorCode: "public-invitation-invalid-data",
        errorKey: 100019,
      };
    }

    let role;

    switch (invitation_type) {
      case "investigator":
        role = "investigator";
        break;
      case "cro":
        role = "admin";
        break;
      case "join_cro":
        role = "admin";
        break;
      default:
        role = "";
    }

    const session = getSession();

    const hashedEmail = await sha256(session.user.email);
    const tmpKeyRes = await AuthService.getTmpApiKey(hashedEmail);

    const xApiKey = tmpKeyRes;

    const onDid = STATE[CurrentEntityState.CURRENT_ENTITY_DATA].did_address;

    console.log("=== onDid === invitation.controller.js === key: 701735 ===");
    console.dir(onDid, { depth: null, colors: true });
    console.log("=================================");

    const presignedPayload = await AuthService.getPresignedPayload(
      onDid,
      role,
      xApiKey
    );

    console.log(
      "=== presignedPayload === invitation.controller.js === key: 878023 ==="
    );
    console.dir(presignedPayload, { depth: null, colors: true });
    console.log("=================================");

    const payload = {
      email,
      firstname,
      lastname,
      contact_name, 
      invitation_type,
      send_by_user_id,
      entity_id,
      investigator_id,
      study_id,
      site_id,
      phone_number,
      delegate: presignedPayload,
    };
    const result = await InvitationService.sendProtectedInvitation(payload);

    if (!Config.IN_PROD) {
      console.log("=== Protected Invitation Result (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("=================================");
    }

    return result;
  } catch (error) {
    console.error(
      "Frontend Controller error in sendProtectedInvitation:",
      error
    );
    return {
      success: false,
      message: "Unexpected error while sending public invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "public-invitation-controller-error",
      errorKey: 100020,
    };
  }
};

/**
 * Retrieves invitations sent by the authenticated user.
 * This is a FRONTEND controller function.
 * @returns {Promise<object>} An array of invitation objects or an error.
 */
const getInvitationsBySender = async () => {
  try {
    const result = await InvitationService.getSentInvitationsBySender();
    return result;
  } catch (error) {
    console.error("Frontend Controller error in getSentInvitations:", error);
    return {
      success: false,
      message: "Unexpected error while fetching sent invitations.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "sent-invitations-controller-error",
      errorKey: 100023,
    };
  }
};

/**
 * Marks an invitation as “joined” when the invited user logs in.
 * This is a FRONTEND controller function.
 * @param {string} invitationId - The ID of the invitation to join.
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const joinInvitation = async (invitationId) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "Missing or invalid invitationId.",
        errorCode: "invitation-join-invalid-id",
        errorKey: 100042,
      };
    }

    const result = await InvitationService.joinInvitation(invitationId);
    if (!Config.IN_PROD) {
      console.log("=== joinInvitation (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("============================================");
    }
    return result;
  } catch (error) {
    console.error("Frontend Controller error in joinInvitation:", error);
    return {
      success: false,
      message: "Unexpected error while joining invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "invitation-join-controller-error",
      errorKey: 100043,
    };
  }
};

/**
 * Confirms an invitation (manager action) once the user has joined.
 * This is a FRONTEND controller function.
 * @param {string} invitationId - The ID of the invitation to confirm.
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const confirmInvitation = async (invitationId) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "Missing or invalid invitationId.",
        errorCode: "invitation-confirm-invalid-id",
        errorKey: 100052,
      };
    }

    const result = await InvitationService.confirmInvitation(invitationId);
    if (!Config.IN_PROD) {
      console.log("=== confirmInvitation (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("===============================================");
    }
    return result;
  } catch (error) {
    console.error("Frontend Controller error in confirmInvitation:", error);
    return {
      success: false,
      message: "Unexpected error while confirming invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "invitation-confirm-controller-error",
      errorKey: 100053,
    };
  }
};

/**
 * Handles the sending of a patient invitation from the frontend.
 * Performs basic validation and forwards to the service.
 * @param {object} patientData - Raw form data for patient invitation.
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const sendPatient = async (patientData) => {
  try {
    if (!patientData) {
      return {
        success: false,
        message: "No patient data provided.",
        errorCode: "patient-invitation-no-data-frontend",
        errorKey: 100062,
      };
    }

    let {
      email,
      firstname,
      lastname,
      phone_number,
      patient_address_street,
      patient_address_number,
      patient_address_country,
      patient_address_city,
      patient_address_postal_code,
      study_id,
      investigator_id,
      site_id,
    } = patientData;

    // Force email to lowercase
    if (typeof email === "string") {
      email = email.toLowerCase();
    }

    // Input validation (mirroring backend controller's checks)
    const missingFields = [];
    if (!email || typeof email !== "string") missingFields.push("email");
    if (!firstname || typeof firstname !== "string")
      missingFields.push("firstname");
    if (!lastname || typeof lastname !== "string")
      missingFields.push("lastname");
    if (!phone_number || typeof phone_number !== "string")
      missingFields.push("phone_number");
    if (!patient_address_street || typeof patient_address_street !== "string")
      missingFields.push("patient_address_street");
    if (!patient_address_number || typeof patient_address_number !== "string")
      missingFields.push("patient_address_number");
    if (!patient_address_country || typeof patient_address_country !== "string")
      missingFields.push("patient_address_country");
    if (!patient_address_city || typeof patient_address_city !== "string")
      missingFields.push("patient_address_city");
    if (
      !patient_address_postal_code ||
      typeof patient_address_postal_code !== "string"
    )
      missingFields.push("patient_address_postal_code");
    if (!study_id || typeof study_id !== "string")
      missingFields.push("study_id");
    if (!investigator_id || typeof investigator_id !== "string")
      missingFields.push("investigator_id");
    if (!site_id || typeof site_id !== "string") missingFields.push("site_id");

    if (missingFields.length > 0) {
      return {
        success: false,
        message: `Missing or invalid fields: [${missingFields.join(", ")}]`,
        errorCode: "patient-invitation-validation-error-frontend",
        errorKey: 100063,
      };
    }

    const payload = {
      email,
      firstname,
      lastname,
      phone_number,
      patient_address_street,
      patient_address_number,
      patient_address_country,
      patient_address_city,
      patient_address_postal_code,
      study_id,
      investigator_id,
      site_id,
    };

    const result = await InvitationService.sendPatient(payload);

    if (!Config.IN_PROD) {
      console.log("=== Patient Invitation Result (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("=================================");
    }

    if (result && typeof result.success === "boolean") {
      return result;
    } else {
      return { success: true, data: result };
    }
  } catch (error) {
    console.error("Frontend Controller error in sendPatient:", error);
    return {
      success: false,
      message: "Unexpected error while sending patient invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "patient-invitation-controller-error",
      errorKey: 100064,
    };
  }
};

/**
 * Frontend controller to accept an investigator invitation.
 * @param {string} invitationId - The ID of the invitation to accept.
 * @returns {Promise<object>} The API response or a standardized error object.
 * invitationId,
 * studyId,
 * siteId,
 * entityId,
 * delegate
 */

const acceptInvestigatorInvitation = async (invitationId, studyId, siteId, entityId, entityDidAddress, delegate) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "Missing or invalid invitationId.",
        errorCode: "accept-investigator-invitation-invalid-id",
        errorKey: 100067,
      };
    }
    const result = await InvitationService.acceptInvestigatorInvitation(
      invitationId,
      studyId,
      siteId,
      entityId,
      entityDidAddress,
      delegate
    );
    if (!Config.IN_PROD) {
      console.log("=== acceptInvestigatorInvitation (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("========================================================");
    }
    
    return result;
  } catch (error) {
    console.error(
      "Frontend Controller error in acceptInvestigatorInvitation:",
      error
    );
    return {
      success: false,
      message: "Unexpected error while accepting investigator invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "accept-investigator-invitation-controller-error",
      errorKey: 100068,
    };
  }
};

/**
 * Frontend controller to decline an investigator invitation.
 * @param {string} invitationId - The ID of the invitation to decline.
 * @returns {Promise<object>} The API response or a standardized error object.
 */
const declineInvestigatorInvitation = async (invitationId) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "Missing or invalid invitationId.",
        errorCode: "decline-investigator-invitation-invalid-id",
        errorKey: 100069,
      };
    }
    const result = await InvitationService.declineInvestigatorInvitation(
      invitationId
    );
    if (!Config.IN_PROD) {
      console.log(
        "=== declineInvestigatorInvitation (Frontend Controller) ==="
      );
      console.dir(result, { depth: null, colors: true });
      console.log("========================================================");
    }
    return result;
  } catch (error) {
    console.error(
      "Frontend Controller error in declineInvestigatorInvitation:",
      error
    );
    return {
      success: false,
      message: "Unexpected error while declining investigator invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "decline-investigator-invitation-controller-error",
      errorKey: 100070,
    };
  }
};

/**
 * Cancels an invitation (logical delete).
 * @param {string} invitationId - The ID of the invitation to cancel.
 * @returns {Promise<object>} API response or a standardized error object.
 */
const cancelInvitation = async (invitationId) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "Missing or invalid invitationId.",
        errorCode: "cancel-invitation-invalid-id",
        errorKey: 100082,
      };
    }

    const result = await InvitationService.cancelInvitation(invitationId);
    
    if (!Config.IN_PROD) {
      console.log("=== cancelInvitation (Frontend Controller) ===");
      console.dir(result, { depth: null, colors: true });
      console.log("================================================");
    }

    return result;
  } catch (error) {
    console.error("Frontend Controller error in cancelInvitation:", error);
    return {
      success: false,
      message: "Unexpected error while canceling invitation.",
      fromError: !Config.IN_PROD ? error.message : null,
      errorCode: "cancel-invitation-controller-error",
      errorKey: 100083,
    };
  }
};

/**
 * Accepte une invitation « participant » pour un utilisateur déjà enregistré.
 * - Génère un participantId côté contrôleur
 * - Appelle le service frontend qui enverra l'invitationId + participantId au backend
 * - Le backend liera l'utilisateur connecté au nouvel enregistrement participants (ou renverra l'existant)
 *
 * @param {string} invitationId - ID de l'invitation « participant »
 * @returns {Promise<object>} Réponse normalisée { success, data?, message? }
 */
const acceptParticipantInvitation = async (invitationId, userId, rawContentInvitation) => {
  try {
    if (!invitationId || typeof invitationId !== "string") {
      return {
        success: false,
        message: "InvitationId manquant ou invalide.",
        errorCode: "accept-participant-invitation-invalid-id",
        errorKey: 100090,
      };
    }

    const session = getSession();
    const { apiToken } = session || {};

     //{ studyId, investigatorId,email, apiToken }
     const studyId = rawContentInvitation.study_id;
     const investigatorId = rawContentInvitation.investigator_id;
     const email = rawContentInvitation.email;

     const payload = {
       studyId,
       investigatorId,
       email,
       apiToken,
     };

     const joined = await AuthService.joinStudyDelegate(payload);

     console.log('=== joined === invitation.controller.js === key: 983772 ===');
     console.dir(joined, { depth: null, colors: true })
     console.log('=================================');

     const participantId = joined?.participantId;

    // 👇 Passa anche userId
    const result = await InvitationService.acceptParticipantInvitation(invitationId, userId, participantId);

    if (!Config.IN_PROD) {
      console.log('=== { invitationId, userId, result } === invitation.controller.js === key: 389924 ===');
      console.dir({ invitationId, userId, result }, { depth: null, colors: true })
      console.log('=================================');
    }

    return result;
  } catch (error) {
    console.error("Erreur contrôleur (frontend) acceptParticipantInvitation:", error);
    return {
      success: false,
      message: "Erreur inattendue lors de l'acceptation de l'invitation participant.",
      fromError: process.env.NODE_ENV !== "production" ? error.message : null,
      errorCode: "accept-participant-invitation-controller-error",
      errorKey: 100091,
    };
  }
};

export default {
  //sendInvitation,
  getInvitationDetails,
  sendPublicInvitation,
  getInvitationsBySender,
  sendProtectedInvitation,
  confirmInvitation,
  joinInvitation,
  sendPatient,
  acceptInvestigatorInvitation,
  declineInvestigatorInvitation,
  cancelInvitation,
  acceptParticipantInvitation,
};
